import {makeAutoObservable} from "mobx";
import UserMasterListService from "../../../services/api/user-master-list-service";
import {rootContainer, TYPE} from "../../../services/container/root";
import IframeService from "../../../services/iframe-service/iframe-service";
import {UserMasterListStore} from "./user-master-list-store";
import {AVAILABLE_USER_PLATFORMS, IUser, USER_PLATFORM} from "../../../models/User";
import IconConnect from "../../../components/icons/icon-connect/icon-connect";
import IconEdit from "../../../components/icons/icon-edit/icon-edit";
import IconEye from "../../../components/icons/icon-eye/icon-eye";
import IconDisconnectChild from "../../../components/icons/icon-disconnect-child/icon-disconnect-child";

export enum UserMasterListActionsListValues {
	LOGIN_AS_VIEWER = "login_as_viewer",
    LOGIN_AS_EDITOR = "login_as_editor",
	SHOW_CONNECTION = "show-connection",
	CONNECT = "connect",
	DISCONNECT = "disconnect"
}


class UserRowStore {
	private _model: IUser;
    private _parentStore: UserMasterListStore;

	constructor(_model: IUser, _parentStore: UserMasterListStore) {
		this._model = _model;
        this._parentStore = _parentStore;
		makeAutoObservable(this);
	}

    private get _iframeService(): IframeService { return rootContainer.get<IframeService>(TYPE.IframService); }

	private _userActionList = [
		{
			label: 'Log in as Viewer', value: UserMasterListActionsListValues.LOGIN_AS_VIEWER,
			icon: IconEye,
			color: 'var(--color-accent-1)',
		},
		{
			label: 'Log in as Editor', value: UserMasterListActionsListValues.LOGIN_AS_EDITOR,
			icon: IconEdit,
			color: 'var(--color-accent-1)'
		},
		{
			label: 'Connect store', value: UserMasterListActionsListValues.CONNECT,
			icon: IconConnect,
			color: 'var(--color-accent-1)'
		},
		{
			label: 'Connected stores', value: UserMasterListActionsListValues.SHOW_CONNECTION,
			icon: IconConnect,
			color: 'var(--color-semantic-success)'
		},
		{
			label: 'Disconnect store', value: UserMasterListActionsListValues.DISCONNECT,
			icon: IconDisconnectChild,
			color: 'var(--color-semantic-danger)'
		}
	]

	public get id(): number { return this._model.id; }

    public get isShop() {
		return !AVAILABLE_USER_PLATFORMS.includes(this._model.platform);
    }

    /**
     * For owners, show the email as the name because it's more informative
     */
    public get nameToShow(): string {
        if (this.isShop) {
            return this._model.name;
        }
        return this._model.email;
    }

	public get email(): string { return this._model.email; }
	public get user_access(): string { return this._model.user_access; }
	public get platform(): USER_PLATFORM { return this._model.platform; }
	public get move_to_plan(): number|null {return this._model.move_to_plan; }
	public get shopify_plan_display_name(): string|undefined { return this._model.shopify_plan_display_name; }
	public get currentPlanColumn():string | number {
		if(this._model.shopify_grandfathered) {
			return 'FREE';
		}

		if( this._model.return_go_plan &&
			this._model.return_go_plan.plan_type === 'system' &&
			this._model.return_go_plan.parent_plan_id &&
			this._model.return_go_plan.plan_type) {

			return this._model.return_go_plan.parent_plan_id ?? (this._model.plan_id ?? 'NONE');
		}
		else {
			return this._model.plan_id ?? 'NONE';
		}
	}
	private get service(): UserMasterListService {return rootContainer.get<UserMasterListService>(TYPE.UserMasterListService);}

	public get getActionList() {

		if(!this._model.is_child && !this._model.has_children) {
			this._userActionList = this._userActionList.filter((action) => {
				return ![
                    UserMasterListActionsListValues.SHOW_CONNECTION,
                    UserMasterListActionsListValues.DISCONNECT
                ].includes(action.value);
			});
		}

		if(this._model.is_child) {
			this._userActionList = this._userActionList.filter((action) => {
                return ![
                    UserMasterListActionsListValues.CONNECT,
                    UserMasterListActionsListValues.SHOW_CONNECTION
                ].includes(action.value);
			});
		}

		if(this._model.has_children) {
			this._userActionList = this._userActionList.filter((action) => {
                return ![
                    UserMasterListActionsListValues.CONNECT,
                    UserMasterListActionsListValues.DISCONNECT
                ].includes(action.value);
			});
		}

		if(!this._model.return_go_plan || this._model.platform === 'portal') {
			this._userActionList = this._userActionList.filter((action) => {
                return ![
                    UserMasterListActionsListValues.CONNECT,
                    UserMasterListActionsListValues.DISCONNECT,
                    UserMasterListActionsListValues.SHOW_CONNECTION
                ].includes(action.value);
            });
		}

		return this._userActionList;
	}

    public async loginAsViewer(): Promise<void> {
        const viewerRole = this._parentStore.viewerRole;
        if (!viewerRole) {
            throw new Error('Viewer role not found. Please ensure roles are loaded.');
        }
        await this.loginAs(viewerRole.id);
    }

    public async loginAsEditor(reason: string): Promise<void> {
        const editorRole = this._parentStore.editorRole;
        if (!editorRole) {
            throw new Error('Editor role not found. Please ensure roles are loaded.');
        }
        await this.loginAs(editorRole.id, reason);
    }

	private async loginAs(roleId: number, reason?: string) {
		this._parentStore.setLoading(true);
		try {
			let res = await this.service.login(this._model.id, roleId, reason);
			if(res.success) {
				this._iframeService.redirectParentFrame('/dashboard');
			}
		}
		catch (e) {
			this._parentStore.setLoading(false);
		}
	}
}

export default UserRowStore;
